/* MyBoost pages — homepage & page sections behaviour (ES module). */
export function clampIndex(index, count) {
  return count <= 0 ? 0 : Math.max(0, Math.min(count - 1, index));
}

export function nearestIndex(scrollLeft, step) {
  return step > 0 ? Math.round(scrollLeft / step) : 0;
}

export function isBannerActive({ start, end }, today) {
  if (start && start > today) return false;
  if (end && end < today) return false;
  return true;
}

// rate is a whole percentage (40 = 40 %); sales × basket × rate already equals the commission in cents
export function estimateCommission({ sales, basket, rate }) {
  const cents = Number(sales) * Number(basket) * Number(rate);
  return Number.isFinite(cents) ? Math.round(cents) : 0;
}

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const todayLocal = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

function initCarousel(section) {
  if (section.dataset.mbaReady === 'true') return;
  section.dataset.mbaReady = 'true';
  const track = section.querySelector('[data-mba-carousel-track]');
  if (!track) return;
  const today = todayLocal();
  const designMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  [...track.children].forEach((slide) => {
    const banner = { start: slide.dataset.mbaStart || '', end: slide.dataset.mbaEnd || '' };
    if (isBannerActive(banner, today) || designMode) {
      slide.hidden = false;
    } else {
      slide.remove();
    }
  });
  const slides = [...track.children];
  const controls = section.querySelector('[data-mba-carousel-controls]');
  if (slides.length === 0) {
    section.hidden = true;
    return;
  }
  if (controls) controls.hidden = slides.length < 2;
  slides.forEach((slide, index) => slide.setAttribute('aria-label', `${index + 1} / ${slides.length}`));
  if (slides.length < 2) return;
  const dots = section.querySelector('[data-mba-carousel-dots]');
  dots.replaceChildren(...slides.map(() => document.createElement('i')));
  const prevButton = section.querySelector('[data-mba-carousel-prev]');
  const nextButton = section.querySelector('[data-mba-carousel-next]');
  const step = () => slides[1].offsetLeft - slides[0].offsetLeft;
  const current = () => nearestIndex(Math.abs(track.scrollLeft), Math.abs(step()));
  const go = (index) => {
    const target = slides[clampIndex(index, slides.length)];
    track.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
  };
  const sync = () => {
    const active = current();
    [...dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    prevButton.setAttribute('aria-disabled', active === 0 ? 'true' : 'false');
    nextButton.setAttribute('aria-disabled', active === slides.length - 1 ? 'true' : 'false');
  };
  prevButton.addEventListener('click', () => {
    if (prevButton.getAttribute('aria-disabled') === 'true') return;
    go(current() - 1);
  });
  nextButton.addEventListener('click', () => {
    if (nextButton.getAttribute('aria-disabled') === 'true') return;
    go(current() + 1);
  });
  let syncPending = false;
  track.addEventListener('scroll', () => {
    if (syncPending) return;
    syncPending = true;
    requestAnimationFrame(() => {
      syncPending = false;
      sync();
    });
  }, { passive: true });
  sync();
}

function initReviews(section) {
  if (section.dataset.mbaReviewsReady === 'true') return;
  section.dataset.mbaReviewsReady = 'true';
  section.querySelectorAll('[data-mba-review-text]').forEach((text) => {
    const button = text.nextElementSibling;
    if (!button || !button.matches('[data-mba-review-more]')) return;
    text.classList.add('is-clamped');
    if (text.scrollHeight <= text.clientHeight + 1) {
      text.classList.remove('is-clamped');
      return;
    }
    button.hidden = false;
    button.addEventListener('click', () => {
      const open = !text.classList.toggle('is-clamped');
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.less : button.dataset.more;
    });
  });
}

function initVideos(section) {
  if (section.dataset.mbaVideosReady === 'true') return;
  section.dataset.mbaVideosReady = 'true';
  const dialog = section.querySelector('[data-mba-video-dialog]');
  const stage = dialog.querySelector('[data-mba-video-stage]');
  let trigger = null;
  const cleanup = () => {
    stage.querySelector('video')?.pause();
    stage.replaceChildren();
    document.documentElement.classList.remove('mba-lock');
    if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
  };
  dialog.addEventListener('close', cleanup);
  section.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mba-video]');
    if (!button) return;
    const template = section.querySelector(`[data-mba-video-template="${button.getAttribute('data-mba-video')}"]`);
    if (!template) return;
    trigger = button;
    stage.replaceChildren(template.content.cloneNode(true));
    dialog.setAttribute('aria-label', button.getAttribute('aria-label') || '');
    document.documentElement.classList.add('mba-lock');
    dialog.showModal();
    stage.querySelector('video')?.play().catch(() => {});
  });
  dialog.querySelector('[data-mba-video-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}

function initSimulator(section) {
  if (section.dataset.mbaReady === 'true') return;
  section.dataset.mbaReady = 'true';
  const sales = section.querySelector('[data-mba-sim-sales]');
  const basket = section.querySelector('[data-mba-sim-basket]');
  const salesOut = section.querySelector('[data-mba-sim-sales-out]');
  const basketOut = section.querySelector('[data-mba-sim-basket-out]');
  const result = section.querySelector('[data-mba-sim-result]');
  const { locale, currency, rate } = section.dataset;
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
    } catch {
      return String(Math.round(cents / 100));
    }
  };
  const update = () => {
    salesOut.textContent = sales.value;
    basketOut.textContent = money(Number(basket.value) * 100);
    result.textContent = money(estimateCommission({ sales: sales.value, basket: basket.value, rate }));
  };
  sales.addEventListener('input', update);
  basket.addEventListener('input', update);
  update();
}

// mba:home-modules-above

export function initHome(scope = document) {
  scope.querySelectorAll('[data-mba-carousel]').forEach(initCarousel);
  scope.querySelectorAll('[data-mba-reviews]').forEach(initReviews);
  scope.querySelectorAll('[data-mba-videos]').forEach(initVideos);
  scope.querySelectorAll('[data-mba-affil-sim]').forEach(initSimulator);
  // mba:home-init
}

if (typeof document !== 'undefined') {
  initHome();
  document.addEventListener('shopify:section:load', (event) => initHome(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector?.('[data-mba-video-dialog][open]')) {
      document.documentElement.classList.remove('mba-lock');
    }
  });
}
